import clsx from 'clsx';
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';

import Button, { ButtonProps } from '@components/Button';
import useEventTracking from '@hooks/useEventTracking';
import { ClickTarget, click } from '@tracking';

export type SubmenuNavProps = ButtonProps & {
  name: string;
  forceOpen?: boolean;
  icon?: (isOpen: boolean) => React.JSX.Element;
  image?: React.JSX.Element;
  isScrollStyle?: boolean;
  openMenuClasses?: string;
  openOnHover: boolean;
  submenuClasses?: string;
};

export interface SubmenuNavRef {
  getSubmenuElement: () => HTMLDivElement | null;
  getNavElement: () => HTMLElement | null;
}

const SubmenuNav = forwardRef<SubmenuNavRef, SubmenuNavProps>(
  (
    {
      className,
      children,
      forceOpen = false,
      icon,
      image,
      isScrollStyle,
      name,
      openMenuClasses,
      openOnHover,
      submenuClasses,
      ...rest
    },
    ref
  ) => {
    const [isOpen, setIsOpen] = useState(false);
    const navRef = useRef<HTMLElement>(null);
    const submenuRef = useRef<HTMLDivElement>(null);
    const { trackEvent } = useEventTracking();

    const effectiveIsOpen = forceOpen || isOpen;

    useImperativeHandle(ref, () => ({
      getSubmenuElement: () => submenuRef.current,
      getNavElement: () => navRef.current,
    }));

    const openOnHoverAttributes =
      openOnHover && !forceOpen
        ? {
            onMouseEnter: () => setTimeout(() => setIsOpen(true), 0),
            onMouseLeave: () => setIsOpen(false),
          }
        : {};

    const handleNavClick = () => {
      if (!forceOpen) {
        setIsOpen((isOpen) => !isOpen);
      }
      trackEvent(click({ clickTarget: ClickTarget.DropDown, clickText: name }));
    };

    useEffect(() => {
      const closeMenuOnClickAway = (event: MouseEvent) => {
        if (!forceOpen && navRef.current && !navRef.current.contains(event.target as Node)) {
          setIsOpen(false);
        }
      };

      document.addEventListener('click', closeMenuOnClickAway);
      return () => {
        document.removeEventListener('click', closeMenuOnClickAway);
      };
    }, [name, forceOpen]);

    return (
      <nav
        ref={navRef}
        id={`${name.toLowerCase()}-submenu-nav`}
        aria-label={name}
        className={clsx(
          { relative: isScrollStyle },
          {
            'z-[1]': !image,
            'z-auto': image,
          }
        )}
        {...openOnHoverAttributes}
      >
        <Button
          className={clsx(className, {
            [openMenuClasses]: effectiveIsOpen,
          })}
          onClick={handleNavClick}
          aria-expanded={effectiveIsOpen ? 'true' : 'false'}
          aria-controls={`${name.toLowerCase()}-submenu-nav-list`}
          {...rest}
        >
          {image}
          <span className={clsx({ 'sr-only': image })}>{name}</span>
          <span className="sr-only"> menu</span>
          {icon && icon(effectiveIsOpen)}
        </Button>
        <div
          ref={submenuRef}
          id={`${name.toLowerCase()}-submenu-nav-list`}
          className={clsx(submenuClasses, 'z-10', {
            hidden: !effectiveIsOpen,
          })}
        >
          {children}
        </div>
      </nav>
    );
  }
);

SubmenuNav.displayName = 'SubmenuNav';

export default SubmenuNav;
